import { Button, Card, CardContent, H2, Input } from "@dune/ui";
import { useEffect, useMemo, useState } from "react";
import {
	useGetConstructionSiteSubmissionOperation,
	useUpdateConstructionSiteReceptionCost,
} from "../../../../api/queries/constructionSiteSubmission.queries";
import { useShouldDisplayHTAmount } from "../../../../hooks/useParticipantToSubmissionForm";
import { useRequiredParams } from "../../../../hooks/useRequiredParams";
import { useReceptionParticipantsView } from "../../../../hooks/useReceptionParticipantsView";
import { SupportingDocumentsControl } from "../../analysis/components/SupportingDocumentsControl";
import { PriceInput } from "../../../../components/inputs/PriceInput";

type Props = { onNext: () => void };

export const ReceptionAnalysisControls = ({ onNext }: Props) => {
	const { constructionSiteId: constructionSiteIdString } = useRequiredParams<{
		constructionSiteId: string;
	}>();
	const constructionSiteId = Number(constructionSiteIdString);
	const { data: operation } =
		useGetConstructionSiteSubmissionOperation(constructionSiteId);
	const { shouldDisplayHT } = useShouldDisplayHTAmount(constructionSiteId);

	const { designers, builders, materials } =
		useReceptionParticipantsView(constructionSiteId);

	const updateReceptionCost =
		useUpdateConstructionSiteReceptionCost(constructionSiteId);

	const planned = useMemo(() => {
		const details = operation?.constructionSite?.constructionSiteDetails;
		return shouldDisplayHT
			? (details?.totalAmountHT ?? 0)
			: (details?.totalAmountTTC ?? 0);
	}, [operation, shouldDisplayHT]);

	const declaredFromParticipants = useMemo(() => {
		const totalAmountPerParticipantType = (participants: typeof designers) =>
			participants.reduce((acc, participant) => {
				const amountToUse = shouldDisplayHT
					? participant.totalFeesHT
					: participant.totalFeesTTC;
				return acc + (amountToUse || 0);
			}, 0);
		return (
			totalAmountPerParticipantType(designers) +
			totalAmountPerParticipantType(builders) +
			totalAmountPerParticipantType(materials)
		);
	}, [designers, builders, materials, shouldDisplayHT]);

	const [declaredEditable, setDeclaredEditable] = useState<number>(0);
	const [currentAction, setCurrentAction] = useState<
		"saveAndNext" | "saveAndClose" | null
	>(null);

	useEffect(() => {
		setDeclaredEditable(
			operation?.constructionSite?.constructionSiteDetails?.finalTotalAmount ??
				declaredFromParticipants,
		);
	}, [operation, declaredFromParticipants]);

	const variation = useMemo(() => {
		if (!planned) return 0;
		return Math.round(((declaredEditable - planned) / planned) * 10000) / 100;
	}, [planned, declaredEditable]);

	const currencyFmt = useMemo(
		() =>
			new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }),
		[],
	);

	const handleSaveAndNext = async (): Promise<void> => {
		setCurrentAction("saveAndNext");
		try {
			await updateReceptionCost.mutateAsync({
				finalTotalAmount: declaredEditable,
			});
			onNext();
		} finally {
			setCurrentAction(null);
		}
	};

	const handleSaveAndClose = async (): Promise<void> => {
		setCurrentAction("saveAndClose");
		try {
			await updateReceptionCost.mutateAsync({
				finalTotalAmount: declaredEditable,
			});
			globalThis.history.back();
		} finally {
			setCurrentAction(null);
		}
	};

	return (
		<div className="flex flex-col gap-6 p-6">
			<h2 className="text-2xl font-medium text-text-secondary-blue">
				Contrôles
			</h2>

			<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
				<Card>
					<CardContent className="p-6 space-y-2">
						<div className="text-sm text-gray-700">
							Cout de construction prévisionnel
						</div>
						<Input
							readOnly
							value={currencyFmt.format(planned)}
							iconOrTextPosition="right"
						/>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-6 space-y-2">
						<div className="text-sm text-gray-700">Coût de construction</div>
						<PriceInput
							value={declaredEditable}
							formatThousands={true}
							iconOrText="€"
							onChange={(val) => {
								setDeclaredEditable(Number(val));
							}}
						/>
						<div className="text-xs text-gray-500">
							Valeur initiale: {currencyFmt.format(declaredFromParticipants)}
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-6 space-y-2">
						<div className="text-sm text-gray-700">Variation</div>
						<Input readOnly value={`${variation.toFixed(2)} %`} />
					</CardContent>
				</Card>
			</div>

			<div className="flex flex-col gap-4">
				<H2>Pièces justificatives</H2>
				<SupportingDocumentsControl context="reception" />
			</div>

			<div className="flex flex-row h-11 items-center justify-between w-full">
				<div />
				<div className="flex flex-row gap-4 items-center">
					<Button
						variant="outline"
						onClick={handleSaveAndClose}
						disabled={updateReceptionCost.isPending || currentAction !== null}
						isLoading={currentAction === "saveAndClose"}
					>
						Enregistrer et fermer
					</Button>
					<Button
						onClick={handleSaveAndNext}
						disabled={updateReceptionCost.isPending || currentAction !== null}
						isLoading={currentAction === "saveAndNext"}
					>
						Suivant
					</Button>
				</div>
			</div>
		</div>
	);
};

export default ReceptionAnalysisControls;
